/**
 * Reading the *text* value of a dynamic property, around an ODA bug.
 *
 * In DrawingWeb 27.7 `OdDbDynBlockReferenceProperty::value()` hands back a variant
 * whose payload is already gone for kDwgText properties — visibility states and
 * lookup ("Consultation") tables. getType() still says kDwgText and isEmpty() says
 * false, but toString() yields "" and getValue4() fails. Numeric, flip and point
 * properties are unaffected. Reported to ODA; until it is fixed, an empty current
 * value would mean the panel cannot show the active state AND undo cannot restore
 * it (there is no old value to write back).
 *
 * Workaround: read the values back from where AutoCAD — and ODA itself — keep them,
 * the block reference's extension dictionary:
 *
 *   <block ref> ▸ extensionDictionary
 *     └ AcDbBlockRepresentation
 *        └ AppDataCache
 *           └ ACAD_ENHANCEDBLOCKDATA        one xrecord per eval-graph node,
 *              ├ "96"  → …, 1 = "Droite"    keyed by node id, a group-1 string
 *              ├ "131" → …, 1 = "1:125"     for every text-valued parameter
 *              └ …
 *
 * ODA rewrites those xrecords synchronously inside setValue(), so they track the
 * live state rather than the state on disk (verified at runtime).
 *
 * The dictionary carries node ids, not property names, and the node → property link
 * (OdDbDynBlockReferenceProperty::getParameter) is not bound in the web build. What
 * holds — verified against customer files — is the ORDER: sorted by node id, the nth
 * text-valued node is the nth text-valued property returned by getBlockProperties().
 * Mind the sort: the dictionary keys are the ids as STRINGS and ODA iterates them
 * lexicographically, which puts "96" after "192" — they are re-sorted numerically here.
 *
 * Read-only and strictly positional, so the caller validates before trusting it: same
 * count on both sides, and every value member of its property's allowed values. Any
 * mismatch discards the whole mapping and we fall back to "unknown state".
 */
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { OpenMode, DictIterType } from "../core/constants";
import { Logger } from "@/logger";

/** DXF group code of a string item inside an xrecord. */
const RESBUF_STRING = 1;

const REPRESENTATION_DICT = "AcDbBlockRepresentation";
const APP_DATA_CACHE_DICT = "AppDataCache";
const ENHANCED_BLOCK_DATA_DICT = "ACAD_ENHANCEDBLOCKDATA";

/** Open a nested dictionary by name, or null when absent. */
function openSubDictionary(
  module: DrawingModuleType,
  dictionary: DrawingModule.OdDbDictionary,
  name: string,
): DrawingModule.OdDbDictionary | null {
  const key = new module.OdString(name);
  try {
    return module.OdDbDictionary.cast(
      dictionary.getAt1(key, OpenMode.kForRead),
    );
  } catch {
    return null;
  } finally {
    deleteObj(key);
  }
}

/** First group-1 string of an xrecord, or null when it holds none. */
function firstStringItem(
  module: DrawingModuleType,
  database: DrawingModule.OdDbDatabase,
  xrecord: DrawingModule.OdDbXrecord,
): string | null {
  const iterator = xrecord.newIterator(database);
  try {
    for (iterator.start(); !iterator.done(); iterator.next()) {
      if (iterator.curRestype() !== RESBUF_STRING) continue;
      const resbuf = iterator.getCurResbuf(database);
      const odValue = resbuf.getString();
      const value = odValue.toString();
      deleteObj(odValue);
      deleteObj(resbuf);
      return value;
    }
    return null;
  } finally {
    deleteObj(iterator);
  }
}

/**
 * Text values cached on a block reference, in eval-graph node order.
 * Returns an empty array when the block carries no such cache (e.g. authored
 * outside AutoCAD) — callers treat that like a failed match.
 */
export function readDynBlockTextValues(
  module: DrawingModuleType,
  entityId: DrawingModule.OdDbObjectId,
): string[] {
  // [node id, value] — collected then sorted, see the header note on ordering.
  const found: Array<[number, string]> = [];
  let entity: DrawingModule.OdDbObject | null = null;

  try {
    entity = entityId.safeOpenObject(OpenMode.kForRead, false);
    const database = entity.database();
    const extensionId = entity.extensionDictionary();
    if (!extensionId || extensionId.isNull()) return [];

    const extensionDict = module.OdDbDictionary.cast(
      extensionId.safeOpenObject(OpenMode.kForRead, false),
    );
    if (!extensionDict) return [];

    const representation = openSubDictionary(
      module,
      extensionDict,
      REPRESENTATION_DICT,
    );
    const cache =
      representation &&
      openSubDictionary(module, representation, APP_DATA_CACHE_DICT);
    const nodes =
      cache && openSubDictionary(module, cache, ENHANCED_BLOCK_DATA_DICT);
    if (!nodes) return [];

    const iterator = nodes.newIterator(DictIterType.kDictSorted);
    try {
      for (; !iterator.done(); iterator.next()) {
        const xrecord = module.OdDbXrecord.cast(
          iterator.getObject(OpenMode.kForRead),
        );
        if (!xrecord) continue;
        const value = firstStringItem(module, database, xrecord);
        if (value === null) continue;

        const odKey = iterator.name();
        const nodeId = Number(odKey.toString());
        deleteObj(odKey);
        // Keys are always node ids; anything else means this is not the cache we
        // think it is, and a positional match would be meaningless.
        if (!Number.isFinite(nodeId)) return [];

        found.push([nodeId, value]);
      }
    } finally {
      deleteObj(iterator);
    }
  } catch (error) {
    Logger.captureException(error as Error, {
      context: "blocks/dynamicRepData.ts : readDynBlockTextValues",
    });
    return [];
  } finally {
    deleteObj(entity);
  }

  return found.sort(([a], [b]) => a - b).map(([, value]) => value);
}
